import { Booking, Expense, Insight, DbState } from './types';
import { logAuditAction, clearAuditLogs, loadDemoAuditLogs } from './utils/auditLogger';
import { clearFocusData, loadDemoFocusData } from './utils/focusService';

const STORAGE_KEY = 'solostack_db_state';

const EMPTY_STATE: DbState = {
  bookings: [],
  expenses: [],
  insights: [],
  streams: []
};

export const DEMO_DATA: DbState = {
  bookings: [
    {
      id: 'b-1',
      type: 'consult',
      price: 150,
      client_name: 'Sarah Jenkins',
      date: '2026-07-18T14:30:00.000Z',
      created_at: '2026-07-18T14:30:00.000Z',
      status: 'paid',
      invoice_number: 'INV-1001',
      due_date: '2026-07-20T00:00:00.000Z'
    },
    {
      id: 'b-2',
      type: 'retreat',
      price: 1200,
      client_name: 'Innovate Corp',
      date: '2026-07-15T09:00:00.000Z',
      created_at: '2026-07-15T09:00:00.000Z',
      status: 'unpaid',
      invoice_number: 'INV-1002',
      due_date: '2026-07-10T00:00:00.000Z'
    },
    {
      id: 'b-3',
      type: 'coffee',
      price: 45,
      client_name: 'David Lee',
      date: '2026-07-19T10:15:00.000Z',
      created_at: '2026-07-19T10:15:00.000Z',
      status: 'unpaid',
      invoice_number: 'INV-1003',
      due_date: '2026-07-28T00:00:00.000Z'
    }
  ],
  expenses: [
    {
      id: 'e-1',
      amount: 85,
      category: 'travel',
      linked_stream: 'retreat',
      note: 'Train ticket to retreat location',
      date: '2026-07-14T16:00:00.000Z',
      created_at: '2026-07-14T16:00:00.000Z'
    },
    {
      id: 'e-2',
      amount: 12,
      category: 'other',
      linked_stream: 'coffee',
      note: 'Meeting drinks with David',
      date: '2026-07-19T10:30:00.000Z',
      created_at: '2026-07-19T10:30:00.000Z'
    },
    {
      id: 'e-3',
      amount: 150,
      category: 'supplies',
      linked_stream: 'consult',
      note: 'Online client booking & CRM software annual subscription',
      date: '2026-07-01T08:00:00.000Z',
      created_at: '2026-07-01T08:00:00.000Z'
    }
  ],
  insights: [
    {
      id: 'i-1',
      title: 'Consult Pricing Optimization',
      note: 'Weekend consultations are heavily booked. Consider raising the consulting rate from $150/hr to $175/hr next month.',
      linked_type: 'booking',
      linked_id: 'b-1',
      created_at: '2026-07-18T18:00:00.000Z'
    },
    {
      id: 'i-2',
      title: 'Coffee Sessions to High-Value Lead Flow',
      note: 'Though low ticket ($45), coffee chats frequently convert into premium consult clients (like David Lee). Always send a high-value checklist summary within 24 hours of the coffee chat to maintain momentum.',
      linked_type: 'general',
      linked_id: '',
      created_at: '2026-07-19T11:00:00.000Z'
    }
  ],
  streams: ['consult', 'retreat', 'coffee']
};

let inMemoryState: DbState | null = null;

function safeGetItem(key: string): string | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(key, value);
    }
  } catch {
    // silently fallback to inMemory
  }
}

function safeRemoveItem(key: string): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // ignore
  }
}

// Initialize DB in LocalStorage if it doesn't exist (defaults to interactive benchmark DEMO_DATA)
export function initDb(): DbState {
  if (inMemoryState) {
    return inMemoryState;
  }
  const existing = safeGetItem(STORAGE_KEY);
  if (!existing) {
    const clone = JSON.parse(JSON.stringify(DEMO_DATA)) as DbState;
    safeSetItem(STORAGE_KEY, JSON.stringify(clone));
    inMemoryState = clone;
    loadDemoFocusData();
    loadDemoAuditLogs();
    return clone;
  }
  try {
    const parsed = JSON.parse(existing) as DbState;
    if (!parsed.streams) {
      parsed.streams = [];
    }
    inMemoryState = parsed;
    return parsed;
  } catch (e) {
    console.error('Error parsing DB state, reinitializing with demo dataset', e);
    const clone = JSON.parse(JSON.stringify(DEMO_DATA)) as DbState;
    safeSetItem(STORAGE_KEY, JSON.stringify(clone));
    inMemoryState = clone;
    return clone;
  }
}

export function saveDb(state: DbState): void {
  inMemoryState = state;
  safeSetItem(STORAGE_KEY, JSON.stringify(state));
}

export function getDbState(): DbState {
  return initDb();
}

export function addStream(stream: string): string[] {
  const state = getDbState();
  if (!state.streams) {
    state.streams = [];
  }
  const cleanStream = stream.trim();
  if (cleanStream && !state.streams.includes(cleanStream)) {
    const updatedState: DbState = {
      ...state,
      streams: [...state.streams, cleanStream]
    };
    saveDb(updatedState);
    logAuditAction({
      actionType: 'CREATE_STREAM',
      entityType: 'Stream',
      details: `Created new revenue stream: "${cleanStream}"`
    });
    return updatedState.streams;
  }
  return state.streams;
}

export function removeStream(stream: string): string[] {
  const state = getDbState();
  if (state.streams) {
    const updatedState: DbState = {
      ...state,
      streams: state.streams.filter(s => s !== stream),
      bookings: (state.bookings || []).filter(b => b.type !== stream),
      expenses: (state.expenses || []).filter(e => e.linked_stream !== stream)
    };
    saveDb(updatedState);
    logAuditAction({
      actionType: 'DELETE_STREAM',
      entityType: 'Stream',
      details: `Removed revenue stream: "${stream}" and cleaned up associated records`
    });
    return updatedState.streams;
  }
  return state.streams || [];
}

export function isDemoDataLoaded(state?: DbState): boolean {
  const current = state || getDbState();
  if (!current) return false;
  return (
    current.bookings?.some(b => b.id === 'b-1' || b.id === 'b-2' || b.id === 'b-3') ||
    current.expenses?.some(e => e.id === 'e-1' || e.id === 'e-2' || e.id === 'e-3') ||
    current.insights?.some(i => i.id === 'i-1' || i.id === 'i-2')
  );
}

export function preloadDemo(): DbState {
  const clone = JSON.parse(JSON.stringify(DEMO_DATA)) as DbState;
  inMemoryState = clone;
  safeSetItem(STORAGE_KEY, JSON.stringify(clone));
  loadDemoFocusData();
  loadDemoAuditLogs();
  logAuditAction({
    actionType: 'PRELOAD_DEMO',
    entityType: 'System',
    details: 'Preloaded demo dataset into workspace'
  });
  return clone;
}

export function clearAllData(): DbState {
  inMemoryState = EMPTY_STATE;
  safeSetItem(STORAGE_KEY, JSON.stringify(EMPTY_STATE));
  safeRemoveItem('solostack_business_settings');
  clearFocusData();
  clearAuditLogs();
  logAuditAction({
    actionType: 'CLEAR_DATA',
    entityType: 'System',
    details: 'Cleared all workspace data and reset system database'
  });
  return EMPTY_STATE;
}


export function addBooking(booking: Omit<Booking, 'id' | 'created_at'>): Booking {
  const state = getDbState();
  const nextInvNum = 1000 + (state.bookings?.length || 0) + Math.floor(Math.random() * 100);
  const newBooking: Booking = {
    status: 'unpaid',
    invoice_number: `INV-${nextInvNum}`,
    ...booking,
    id: 'b-' + Math.random().toString(36).substr(2, 9),
    created_at: new Date().toISOString()
  };
  const updatedState: DbState = {
    ...state,
    bookings: [newBooking, ...(state.bookings || [])]
  };
  saveDb(updatedState);
  logAuditAction({
    actionType: 'CREATE_BOOKING',
    entityType: 'Booking',
    entityId: newBooking.id,
    details: `Created booking for "${newBooking.client_name}" (${newBooking.type}) - Amount: $${newBooking.price} (${newBooking.invoice_number})`
  });
  return newBooking;
}

export function updateBookingStatus(id: string, status: 'paid' | 'unpaid' | 'cancelled'): Booking | null {
  const state = getDbState();
  let updatedBooking: Booking | null = null;
  const updatedBookings = (state.bookings || []).map(b => {
    if (b.id === id) {
      updatedBooking = { ...b, status };
      return updatedBooking;
    }
    return b;
  });

  if (updatedBooking) {
    const updatedState: DbState = {
      ...state,
      bookings: updatedBookings
    };
    saveDb(updatedState);
    logAuditAction({
      actionType: 'UPDATE_BOOKING_STATUS',
      entityType: 'Booking',
      entityId: id,
      details: `Updated booking invoice ${(updatedBooking as Booking).invoice_number} status to "${status.toUpperCase()}"`
    });
    return updatedBooking;
  }
  return null;
}

export function addExpense(expense: Omit<Expense, 'id' | 'created_at'>): Expense {
  const state = getDbState();
  const newExpense: Expense = {
    ...expense,
    id: 'e-' + Math.random().toString(36).substr(2, 9),
    created_at: new Date().toISOString()
  };
  const updatedState: DbState = {
    ...state,
    expenses: [newExpense, ...(state.expenses || [])]
  };
  saveDb(updatedState);
  logAuditAction({
    actionType: 'CREATE_EXPENSE',
    entityType: 'Expense',
    entityId: newExpense.id,
    details: `Logged expense of $${newExpense.amount} (${newExpense.category}) linked to stream "${newExpense.linked_stream}": ${newExpense.note || 'No note'}`
  });
  return newExpense;
}

export function addInsight(insight: Omit<Insight, 'id' | 'created_at'>): Insight {
  const state = getDbState();
  const newInsight: Insight = {
    ...insight,
    id: 'i-' + Math.random().toString(36).substr(2, 9),
    created_at: new Date().toISOString()
  };
  const updatedState: DbState = {
    ...state,
    insights: [newInsight, ...(state.insights || [])]
  };
  saveDb(updatedState);
  logAuditAction({
    actionType: 'CREATE_INSIGHT',
    entityType: 'Insight',
    entityId: newInsight.id,
    details: `Created strategic insight: "${newInsight.title}"`
  });
  return newInsight;
}

export function clearAllLedgerData(): DbState {
  const cleanState: DbState = {
    bookings: [],
    expenses: [],
    insights: [],
    streams: ['consult', 'retainer', 'product', 'design']
  };
  saveDb(cleanState);
  logAuditAction({
    actionType: 'CLEAR_DATA',
    entityType: 'System',
    details: 'Reset ledger to clean empty state (removed all demo records)'
  });
  return cleanState;
}

export function clearDemoData(): DbState {
  const state = getDbState();
  // Filter out demo items that have IDs b-1, b-2, b-3 or e-1, e-2, e-3
  const demoBookingIds = new Set(['b-1', 'b-2', 'b-3']);
  const demoExpenseIds = new Set(['e-1', 'e-2', 'e-3']);
  const demoNoteSubstrings = ['online client booking & crm software', 'meeting drinks with david', 'train ticket to retreat'];

  const cleanedExpenses = (state.expenses || []).filter(e => {
    if (demoExpenseIds.has(e.id)) return false;
    const noteLower = (e.note || '').toLowerCase();
    if (demoNoteSubstrings.some(s => noteLower.includes(s))) return false;
    return true;
  });

  const cleanedBookings = (state.bookings || []).filter(b => {
    if (demoBookingIds.has(b.id)) return false;
    if (b.client_name === 'Sarah Jenkins' || b.client_name === 'Innovate Corp' || b.client_name === 'David Lee') return false;
    return true;
  });

  const updatedState: DbState = {
    ...state,
    bookings: cleanedBookings,
    expenses: cleanedExpenses
  };
  saveDb(updatedState);
  logAuditAction({
    actionType: 'CLEAR_DATA',
    entityType: 'System',
    details: 'Removed pre-populated demo seed transactions from ledger'
  });
  return updatedState;
}

export function deleteBooking(id: string): void {
  const state = getDbState();
  const target = (state.bookings || []).find(b => b.id === id);
  const clientInfo = target ? `for "${target.client_name}" (${target.invoice_number})` : `(ID: ${id})`;
  const updatedState: DbState = {
    ...state,
    bookings: (state.bookings || []).filter(b => b.id !== id),
    insights: (state.insights || []).map(i => 
      i.linked_type === 'booking' && i.linked_id === id 
        ? { ...i, linked_type: 'general', linked_id: '' } 
        : i
    )
  };
  saveDb(updatedState);
  logAuditAction({
    actionType: 'DELETE_BOOKING',
    entityType: 'Booking',
    entityId: id,
    details: `Deleted booking record ${clientInfo}`
  });
}

export function deleteExpense(id: string): void {
  const state = getDbState();
  const target = (state.expenses || []).find(e => e.id === id);
  const expInfo = target ? `$${target.amount} (${target.category})` : `(ID: ${id})`;
  const updatedState: DbState = {
    ...state,
    expenses: (state.expenses || []).filter(e => e.id !== id),
    insights: (state.insights || []).map(i => 
      i.linked_type === 'expense' && i.linked_id === id 
        ? { ...i, linked_type: 'general', linked_id: '' } 
        : i
    )
  };
  saveDb(updatedState);
  logAuditAction({
    actionType: 'DELETE_EXPENSE',
    entityType: 'Expense',
    entityId: id,
    details: `Deleted expense record ${expInfo}`
  });
}

export function deleteInsight(id: string): void {
  const state = getDbState();
  const target = (state.insights || []).find(i => i.id === id);
  const titleInfo = target ? `"${target.title}"` : `(ID: ${id})`;
  const updatedState: DbState = {
    ...state,
    insights: (state.insights || []).filter(i => i.id !== id)
  };
  saveDb(updatedState);
  logAuditAction({
    actionType: 'DELETE_INSIGHT',
    entityType: 'Insight',
    entityId: id,
    details: `Deleted strategic insight ${titleInfo}`
  });
}

/**
 * Reconcile & batch import verified transactions (Bookings & Expenses) into the local/cloud database
 */
export function addBatchReconciliationItems(params: {
  newBookings: Array<Omit<Booking, 'id' | 'created_at'>>;
  newExpenses: Array<Omit<Expense, 'id' | 'created_at'>>;
  newStreams?: string[];
}): DbState {
  const state = getDbState();
  let currentStreams = [...(state.streams || [])];

  // Merge newly detected streams
  if (params.newStreams && params.newStreams.length > 0) {
    for (const stream of params.newStreams) {
      const clean = stream.trim();
      if (clean && !currentStreams.includes(clean)) {
        currentStreams.push(clean);
      }
    }
  }

  // Create real Bookings with invoice numbers
  let baseInv = 1000 + (state.bookings?.length || 0);
  const createdBookings: Booking[] = params.newBookings.map((b, idx) => {
    return {
      status: b.status || 'paid',
      invoice_number: b.invoice_number || `INV-${baseInv + idx + 1}`,
      ...b,
      id: 'b-' + Math.random().toString(36).substr(2, 9),
      created_at: new Date().toISOString()
    };
  });

  // Create real Expenses
  const createdExpenses: Expense[] = params.newExpenses.map(e => {
    return {
      ...e,
      id: 'e-' + Math.random().toString(36).substr(2, 9),
      created_at: new Date().toISOString()
    };
  });

  const updatedState: DbState = {
    ...state,
    streams: currentStreams,
    bookings: [...createdBookings, ...(state.bookings || [])],
    expenses: [...createdExpenses, ...(state.expenses || [])]
  };

  saveDb(updatedState);

  logAuditAction({
    actionType: 'IMPORT_CSV',
    entityType: 'System',
    details: `AI Reconciled & Imported ${createdBookings.length} booking(s) and ${createdExpenses.length} expense(s)`
  });

  return updatedState;
}
